import {defaultConditon} from '@/context/Promotion/PromotionContext'
import {isStartDateLessThanCurrentDate} from '@/helpers/dateUtils'
import {ConditionGroup, PromotionCondition, PromotionStep, PromotionType} from '@/types/promotion'
import {SegmentTypes} from '../constants/promotionConstants'
import {deepMergeNoArray} from '../misc'
import {getValidPromotionConditions} from './promotionsValidationSchema'

const isEmpty = val => val == null || (typeof val === 'string' && !val.trim())

const hasConsistentRewardTypes = promotionConditions => {
  const rewardTypes = promotionConditions.map(c => c?.rewards?.[0]?.type).filter(Boolean)
  return new Set(rewardTypes).size <= 1
}

const isStepSetupInvalid = (promotion, promotionConfig) => {
  if (!promotion?.name) return true

  const condition = promotion?.promotionConditions?.[0]
  if (!condition) return true

  const {validCount, totalCount} = getValidPromotionConditions(promotion.promotionConditions, promotion.promotionType)

  //When we save a promotion, we want to ensure that all the conditions are valid
  if (validCount !== totalCount) {
    return true
  }

  const conditions = promotion?.promotionConditions || []
  if (conditions.length > 1 && !hasConsistentRewardTypes(conditions)) {
    return true
  }

  // Check if any reward's discount percentage exceeds the maximum allowed
  const maxDiscountPercentage = promotionConfig?.maxDiscountPercentage || 0
  const hasExceededMaxDiscount = conditions.some(condition => {
    return condition?.rewards?.some(reward => {
      return reward?.discountValue > maxDiscountPercentage
    })
  })

  const promotionType = promotion?.promotionType
  const shouldCheckForMaxDiscount = [PromotionType.BUY_SINGLE_PRODUCT, PromotionType.BUY_MULTIPLE_PRODUCTS].includes(
    promotionType
  )

  if (shouldCheckForMaxDiscount && hasExceededMaxDiscount) {
    return true
  }
  const segmentType = promotion?.segments?.selectedSegments?.[0]?.selectionType ?? SegmentTypes.ALL_CUSTOMERS
  if (
    promotion?.segments?.selectedSegments?.[0]?.segmentsView?.length === 0 &&
    segmentType !== SegmentTypes.ALL_CUSTOMERS
  ) {
    return true
  }
  return false
}

const isStepDisplayInvalid = (promotion, languages) => {
  if (isEmpty(promotion?.bannerUrl)) return true
  if (promotion?.routeTypeId === 'Empty' || promotion?.routeTypeId === '') return true

  if (promotion?.bannerType === 'clickable' && !promotion?.landingPage) {
    return true
  }

  if (
    promotion?.displayOrder === false ||
    promotion?.displayOrder === 0 ||
    promotion?.displayOrder === null ||
    promotion?.displayOrder === undefined
  ) {
    return true
  }

  const text = promotion?.text || {}
  const isTextComplete = lang => {
    const localized = text[lang]
    return localized?.title && localized?.bannerTag && localized?.productTag
  }

  const langCodes = languages?.length ? languages.map(l => l.code) : Object.keys(text)

  return !langCodes.every(isTextComplete)
}

const isStepAttributesInvalid = promotion => {
  if (!promotion?.activeStartDate || !promotion?.activeEndDate) {
    return true
  }

  if (
    promotion?.activeStartDate > promotion?.activeEndDate ||
    isStartDateLessThanCurrentDate(promotion.activeStartDate)
  ) {
    return true
  }

  return false
}

export const getIsNextDisabled = (step, promotion, languages, promotionConfig) => {
  switch (step) {
    case PromotionStep.SETUP:
      return isStepSetupInvalid(promotion, promotionConfig)
    case PromotionStep.DISPLAY:
      return isStepDisplayInvalid(promotion, languages)
    case PromotionStep.ATTRIBUTES:
      return isStepAttributesInvalid(promotion)
    default:
      return false
  }
}

//For drafts we can save with partial conditions but don't want to return them in the draft
export const parsePromotionDraft = promotion => {
  const parsedPromotion = JSON.parse(JSON.stringify(promotion))
  const {hasValid} = getValidPromotionConditions(promotion.promotionConditions, promotion.promotionType)
  parsedPromotion.promotionConditions = hasValid ? promotion.promotionConditions : []
  return parsedPromotion
}

export function mergePromotionConditions(
  promotionConditions: PromotionCondition[],
  updates: Partial<PromotionCondition>
): PromotionCondition[] {
  return promotionConditions.map(pc => {
    if (pc.sequence !== updates.sequence) return pc

    // 1) Merge top-level fields
    const mergedPC: PromotionCondition = deepMergeNoArray(pc, updates)

    // 2) Merge nested conditionGroup.conditions by index, if provided
    if (updates.conditionGroup?.conditions) {
      const originalConds = pc.conditionGroup?.conditions ?? []
      const incomingConds = updates.conditionGroup.conditions

      mergedPC.conditionGroup = {
        ...(pc.conditionGroup ?? {}),
        ...updates.conditionGroup,
        conditions: originalConds.length
          ? originalConds.map((orig, idx) => deepMergeNoArray(orig, incomingConds[idx] ?? {}))
          : incomingConds,
      } as ConditionGroup
    }

    // 3) Merge rewards array by index, if provided
    if (updates.rewards) {
      const originalRewards = pc.rewards ?? []
      const incomingRewards = updates.rewards

      mergedPC.rewards = originalRewards.length
        ? originalRewards.map((orig, idx) => deepMergeNoArray(orig, incomingRewards[idx] ?? {}))
        : incomingRewards
    }

    return mergedPC
  })
}

export const createPromotionConditions = (type: PromotionType) => {
  switch (type) {
    case PromotionType.BUY_SINGLE_PRODUCT:
      return [
        {
          sequence: 1,
          conditionGroup: {
            conditions: [defaultConditon],
          },
        },
      ]
    case PromotionType.BUY_MULTIPLE_PRODUCTS:
      return [
        {
          sequence: 1,
          conditionGroup: {
            conditions: [defaultConditon, defaultConditon],
          },
        },
      ]
    default:
      return [
        {
          sequence: 1,
        },
      ]
  }
}
